import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { UserCheck, Check, X, Building2 } from "lucide-react";

const tiers = [
  { v: "standard", label: "Standard" },
  { v: "tier_a", label: "Tier A" },
  { v: "tier_b", label: "Tier B" },
];

export default function Applications() {
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [tier, setTier] = useState({});
  const [acRef, setAcRef] = useState({});

  const load = () => api.get("/admin/applications", { params: filter ? { status: filter } : {} }).then(({ data }) => setApps(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const decide = async (id, decision) => {
    try {
      const app = apps.find((x) => x.id === id);
      const ref = acRef[id] ?? app?.suggested_sage_ref ?? "";
      await api.post(`/admin/applications/${id}/decision`, { decision, price_tier: tier[id] || "standard", sage_account_ref: ref });
      toast.success(decision === "approved" ? "Account approved" : "Application rejected");
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <h1 className="font-serif-display text-3xl font-bold text-slate-50">Trade Applications</h1>
      <p className="mt-1 text-sm text-[#94A3B8]">Review and approve retailer accounts. Assign a pricing tier on approval.</p>

      <div className="mt-6 flex gap-2">
        {[["pending", "Pending"], ["approved", "Approved"], ["rejected", "Rejected"], ["", "All"]].map(([v, l]) => (
          <button key={l} data-testid={`apps-filter-${l.toLowerCase()}`} onClick={() => setFilter(v)}
            className={`rounded-full border px-4 py-2 text-sm ${filter === v ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-[#2A2E39] text-slate-300"}`}>{l}</button>
        ))}
      </div>

      <div className="mt-8 space-y-4">
        {apps.length === 0 && <div className="rounded-2xl border border-[#2A2E39] p-12 text-center text-[#64748B]">No applications in this view.</div>}
        {apps.map((a) => (
          <div key={a.id} data-testid={`application-${a.id}`} className="rounded-2xl border border-[#2A2E39] bg-[#1A1D24] p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#D4AF37]/10 text-[#D4AF37]"><Building2 className="h-5 w-5" /></div>
                <div>
                  <div className="text-lg font-semibold text-slate-100">{a.company_name}</div>
                  <div className="text-sm text-[#94A3B8]">{a.name} • {a.email} • {a.phone}</div>
                  {a.vat_number && <div className="text-xs text-[#64748B] font-mono-data">VAT {a.vat_number}</div>}
                  {a.address && <div className="mt-1 text-xs text-[#94A3B8]">{a.address}</div>}
                  {a.message && <div className="mt-2 max-w-xl text-sm text-[#94A3B8]">“{a.message}”</div>}
                </div>
              </div>
              <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${a.status === "approved" ? "border-emerald-500/40 text-emerald-400" : a.status === "rejected" ? "border-rose-500/40 text-rose-400" : "border-amber-500/40 text-amber-400"}`}>{a.status.toUpperCase()}</span>
            </div>

            {a.status === "pending" && (
              <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-[#2A2E39] pt-5">
                <span className="text-xs text-[#64748B]">Pricing tier:</span>
                <div className="flex gap-1">
                  {tiers.map((t) => (
                    <button key={t.v} data-testid={`tier-${a.id}-${t.v}`} onClick={() => setTier((s) => ({ ...s, [a.id]: t.v }))}
                      className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${(tier[a.id] || "standard") === t.v ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-[#2A2E39] text-slate-300"}`}>{t.label}</button>
                  ))}
                </div>
                <input data-testid={`acref-${a.id}`} value={acRef[a.id] ?? a.suggested_sage_ref ?? ""} onChange={(e) => setAcRef((s) => ({ ...s, [a.id]: e.target.value }))}
                  placeholder="Sage A/C Ref"
                  className="w-32 rounded-full border border-[#2A2E39] bg-[#0B0C0E] px-3 py-1.5 text-xs text-slate-100 placeholder:text-[#64748B] focus:border-[#D4AF37]/60 focus:outline-none" />
                {a.suggested_sage_ref && (
                  <span data-testid={`sage-match-${a.id}`} className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-400">
                    Matched to {a.suggested_sage_ref} by {a.sage_match_by}
                  </span>
                )}
                <div className="ml-auto flex gap-2">
                  <button data-testid={`approve-${a.id}`} onClick={() => decide(a.id, "approved")} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-4 py-2 text-sm font-semibold text-emerald-400 hover:bg-emerald-500/25"><Check className="h-4 w-4" /> Approve</button>
                  <button data-testid={`reject-${a.id}`} onClick={() => decide(a.id, "rejected")} className="inline-flex items-center gap-1.5 rounded-full bg-rose-500/15 px-4 py-2 text-sm font-semibold text-rose-400 hover:bg-rose-500/25"><X className="h-4 w-4" /> Reject</button>
                </div>
              </div>
            )}
            {a.status === "approved" && <div className="mt-3 text-xs text-[#64748B]">Tier: <span className="text-[#D4AF37]">{a.price_tier}</span>{a.sage_account_ref ? <> · Sage A/C: <span className="font-mono-data text-slate-200">{a.sage_account_ref}</span></> : <> · <span className="text-amber-400">no Sage A/C Ref</span></>}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
